import {
  BarChart3,
  Contact,
  FileText,
  Layers,
  Plus,
  Receipt,
  TrendingDown,
  Wallet,
} from 'lucide-react';

import {
  AppFrame,
  FrameBadge,
  FrameButton,
  FramePageHeader,
  type BadgeTone,
  type PanelItem,
} from './app-frame';
import { ScaledCanvas } from './scaled-canvas';
import { SectionLabel } from './section-label';

/*
 * A tela de Honorários do CRM (features/billing/pages/finance-page), com os
 * mesmos indicadores, filtros e lista de faturas. Dados fictícios.
 */

const panelItems: PanelItem[] = [
  { label: 'Financeiro', icon: Receipt, active: true, nested: true },
  { label: 'Equipe', icon: Contact, nested: true },
];

const tiles: Array<{ label: string; value: string; alert?: boolean }> = [
  { label: 'Em aberto', value: 'R$ 38.400,00' },
  { label: 'Vencidas', value: 'R$ 2.500,00', alert: true },
  { label: 'Recebido no mês', value: 'R$ 52.100,00' },
  { label: 'Horas a faturar', value: '41h 30min' },
];

const invoices: Array<[string, string, string, BadgeTone, string]> = [
  ['FAT-2026-0142', 'Marina Costa · vence 10/10/2026', 'R$ 4.800,00', 'success', 'Paga'],
  [
    'FAT-2026-0141',
    'Grupo Vértice · vence 15/10/2026',
    'R$ 12.000,00',
    'warning',
    'Parcialmente paga',
  ],
  ['FAT-2026-0140', 'João Almeida · vence 28/09/2026', 'R$ 2.500,00', 'info', 'Enviada'],
  ['FAT-2026-0139', 'Fernanda Lima · vence 20/10/2026', 'R$ 6.300,00', 'info', 'Enviada'],
  ['FAT-2026-0138', 'Carlos Nunes', 'R$ 3.150,00', 'neutral', 'Rascunho'],
];

const filters = ['Todas', 'Rascunho', 'Enviada', 'Parcialmente paga', 'Paga', 'Cancelada'];

const pillars = [
  { icon: FileText, title: 'Faturas', text: 'Numeradas, com PDF e envio por e-mail ao cliente.' },
  {
    icon: Wallet,
    title: 'Recebimentos',
    text: 'Parcelamento e baixa de cada pagamento, ligados ao cliente.',
  },
  {
    icon: TrendingDown,
    title: 'Despesas',
    text: 'Custos do escritório e reembolsáveis no mesmo caixa.',
  },
  {
    icon: BarChart3,
    title: 'Relatórios',
    text: 'Faturamento e horas do período, também por filial.',
  },
];

function HonorariosPage() {
  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <FramePageHeader
        eyebrow="Financeiro"
        title="Honorários"
        description="Faturas do escritório, do rascunho ao recebimento."
        actions={
          <>
            <FrameButton variant="outline">
              <BarChart3 aria-hidden="true" /> Relatórios
            </FrameButton>
            <FrameButton variant="outline">
              <Layers aria-hidden="true" /> Parcelar
            </FrameButton>
            <FrameButton>
              <Plus aria-hidden="true" /> Nova fatura
            </FrameButton>
          </>
        }
      />

      <div className="grid grid-cols-4 gap-3">
        {tiles.map(({ label, value, alert }) => (
          <div
            key={label}
            className="border-outline-variant bg-surface-container-lowest rounded-xl border p-4"
          >
            <p className="text-on-surface-variant text-[11px] font-semibold uppercase tracking-wider">
              {label}
            </p>
            <p className={`mt-1 text-xl font-semibold ${alert ? 'text-error' : 'text-on-surface'}`}>
              {value}
            </p>
          </div>
        ))}
      </div>

      <div className="border-outline-variant bg-surface-container-lowest inline-flex rounded-lg border p-0.5">
        {filters.map((filter, index) => (
          <span
            key={filter}
            className={`rounded-md px-3 py-1.5 text-[13px] font-medium ${
              index === 0 ? 'bg-primary-container text-primary' : 'text-on-surface-variant'
            }`}
          >
            {filter}
          </span>
        ))}
      </div>

      <ul className="border-outline-variant bg-surface-container-lowest divide-outline-variant divide-y rounded-xl border">
        {invoices.map(([number, client, total, tone, status]) => (
          <li key={number} className="flex items-center gap-3 px-4 py-3">
            <p className="w-32 text-sm font-semibold">{number}</p>
            <p className="text-on-surface-variant min-w-0 flex-1 truncate text-sm">{client}</p>
            <p className="text-sm font-semibold">{total}</p>
            <FrameBadge tone={tone}>{status}</FrameBadge>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function FinanceSection() {
  return (
    <section aria-labelledby="lp-finance-title" className="bg-(--lp-soft) py-24 sm:py-32">
      <div className="mx-auto max-w-[1180px] px-5 sm:px-6">
        <div className="grid items-end gap-6 lg:grid-cols-[1.3fr_1fr]" data-reveal>
          <div>
            <SectionLabel>Financeiro</SectionLabel>
            <h2
              id="lp-finance-title"
              className="text-(--lp-mute) mt-5 text-balance text-[clamp(32px,4.4vw,52px)] font-medium leading-[1.06] tracking-[-0.04em]"
            >
              Honorários faturados, <span className="text-(--lp-ink)">caixa sob controle.</span>
            </h2>
          </div>
          <p className="text-(--lp-body) max-w-[420px] text-[16px] leading-relaxed lg:justify-self-end">
            O financeiro ligado aos clientes e aos processos que geram a receita. Sem planilha
            paralela e sem retrabalho no fim do mês.
          </p>
        </div>

        <div className="mt-14" data-reveal>
          <ScaledCanvas
            width={1280}
            height={640}
            minScale={0.5}
            label="Tela de Honorários do Sancus, com valores em aberto, vencidos, recebidos no mês, horas a faturar e a lista de faturas. Dados fictícios."
            className="border-outline-variant rounded-[20px] border bg-white shadow-[0_40px_100px_-40px_rgb(12_80_227/0.35)]"
          >
            <AppFrame area="Gestão" items={panelItems} section="Financeiro">
              <HonorariosPage />
            </AppFrame>
          </ScaledCanvas>
        </div>

        <ul className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {pillars.map(({ icon: Icon, title, text }) => (
            <li key={title} data-reveal>
              <Icon size={20} className="text-primary" />
              <h3 className="text-(--lp-ink) mt-4 text-[17px] font-medium">{title}</h3>
              <p className="text-(--lp-body) mt-1.5 text-[15px] leading-relaxed">{text}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
